import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLoaderData, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { setIsProfileCompleted } from "../../../application/slices/auth";
import DefaultApiService from "../../../infrastructure/api/default";
import { wrapFormikHandleChange } from "../../../infrastructure/utils/formatters";
import IconComponent from "../../components/shared/icon";
import JalaliDatePicker from "../../components/shared/jalali-datepicker";
import FileUploader from "../../components/shared/uploader/FileUploader";
import { formatLocalDateForApi } from "../../middleware/middleware";

// Get base URL for file previews
const getBaseUrl = () => {
  return window.globalConfig?.API_URL || "";
};

const parseProfileBirthDate = (value) => {
  if (!value || value === "0001-01-01T00:00:00") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const validationSchema = Yup.object().shape({
  userName: Yup.string().required("لطفا این فیلد را پر کنید."),
  firstName: Yup.string().required("لطفا این فیلد را پر کنید."),
  lastName: Yup.string().required("لطفا این فیلد را پر کنید."),
  nationalCode: Yup.string().required("لطفا این فیلد را پر کنید."),
  fatherName: Yup.string().required("لطفا نام پدر را وارد کنید."),
  birthDate: Yup.mixed().required("لطفا تاریخ تولد را وارد کنید."),
  email: Yup.string().required("لطفا این فیلد را پر کنید."),
  mobile: Yup.string().required("لطفا این فیلد را پر کنید."),
  gender: Yup.number().min(1, "لطفا جنسیت را انتخاب کنید."),
  agreementKind: Yup.string().required("لطفا نوع شخص را انتخاب کنید."),
  address: Yup.string(),
  provinceId: Yup.number().nullable(),
  city: Yup.string(),
  telephon: Yup.string(),
  postalCode: Yup.string()
    .max(10, "کد پستی باید حداکثر 10 رقم باشد")
    .matches(/^\d*$/, "کد پستی باید فقط شامل اعداد باشد"),
  nationalCardUrl: Yup.string(),
  nationalCardBackUrl: Yup.string(),
  birthCertificateUrl: Yup.string(),
  description: Yup.string(),
  companyId: Yup.number(),
});

const ProfilePage = () => {
  const profileData = useLoaderData();
  const profile = profileData?.data || profileData; // Handle both ApiResult and direct data

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedProvinceId, setSelectedProvinceId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [provincesRes, citiesRes] = await Promise.all([
          DefaultApiService.getApi("Profile/GetProvinces"),
          DefaultApiService.getApi("Profile/GetCities"),
        ]);
        if (provincesRes?.isSuccess && provincesRes?.data) setProvinces(provincesRes.data);
        if (citiesRes?.isSuccess && citiesRes?.data) setAllCities(citiesRes.data);
      } catch (error) {
        console.error("خطا در دریافت استان‌ها و شهرها:", error);
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (profile?.provinceId) setSelectedProvinceId(profile.provinceId);
  }, [profile?.provinceId]);

  const filteredCities = selectedProvinceId
    ? allCities.filter((c) => c.provinceId === parseInt(selectedProvinceId))
    : [];

  const onNavigateToDashboard = () => {
    const defaultUrl = window.localStorage.getItem("defaultUrl");
    if (defaultUrl && typeof defaultUrl === "string" && defaultUrl.trim() !== "") {
      navigate(defaultUrl);
    } else {
      navigate("/SLN/Customer");
    }
  };

  const isCustomerUser = () => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      const roleId = Number(u?.roleId ?? u?.RoleId);
      const defaultUrl = (u?.defaultUrl || u?.DefaultUrl || localStorage.getItem("defaultUrl") || "")
        .toString()
        .toLowerCase();
      return roleId === 4 || defaultUrl.includes("/sln/customer");
    } catch {
      return false;
    }
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    console.log("مقادیر فرم قبل از ارسال:", formValue);
    try {
      const payload = {
        ...formValue,
        birthDate: formatLocalDateForApi(formValue.birthDate),
      };
      const res = await DefaultApiService.postApi(
        "Profile/CompleteProfile",
        payload
      );
      if (res.isSuccess) {
        dispatch(setIsProfileCompleted(true));
        toast.success(
          isCustomerUser()
            ? "پروفایل ذخیره شد. اکنون درخواست نیروگاه را ثبت کنید."
            : res.message
        );
        if (isCustomerUser()) {
          navigate("/SLN/Customer/Requests/create");
        } else {
          onNavigateToDashboard();
        }
      } else {
        toast.error(res.message);
      }
      setLoading(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Avatar>
                  <IconComponent name="user" width="16px" height="16px" />
                </Avatar>

                <Typography variant="body1" fontWeight={600}>
                  پروفایل
                </Typography>
              </Box>
            }
          />
          <CardContent sx={{ mt: 2 }}>
            <Formik
              initialValues={{
                userName: profile?.userName || "",
                firstName: profile?.firstName || "",
                lastName: profile?.lastName || "",
                nationalCode: profile?.nationalCode || "",
                fatherName: profile?.fatherName || "",
                birthDate: parseProfileBirthDate(profile?.birthDate),
                gender: profile?.gender || 0,
                agreementKind: profile?.agreementKind || "Real",
                mobile: profile?.mobile || "",
                email: profile?.email || "",
                telephon: profile?.telephon || "",
                address: profile?.address || "",
                provinceId: profile?.provinceId ?? null,
                province: profile?.province || "",
                city: profile?.city || "",
                telephon: profile?.telephon || "",
                postalCode: profile?.postalCode || "",
                nationalCardUrl: profile?.nationalCardUrl || "",
                nationalCardBackUrl: profile?.nationalCardBackUrl || "",
                birthCertificateUrl: profile?.birthCertificateUrl || "",
                description: profile?.description || "",
                companyId: profile?.companyId || 1,
              }}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                  disabled:
                    profile?.status?.[name] === "Disabled" ? true : false,
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight={700}>
                          اطلاعات هویتی
                        </Typography>
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="کد ملی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("nationalCode")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="نام پدر"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("fatherName")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <JalaliDatePicker
                          label="تاریخ تولد"
                          value={values.birthDate}
                          onChange={(date) => setFieldValue("birthDate", date)}
                          error={errors.birthDate && touched.birthDate}
                          helperText={
                            errors.birthDate && touched.birthDate && errors.birthDate
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="نام کاربری"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("userName")}
                          disabled
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={3}
                        sx={{
                          display:
                            profile?.status?.firstName === "Hide"
                              ? "none"
                              : "block",
                        }}
                      >
                        <TextField
                          label="نام"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("firstName")}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={3}
                        sx={{
                          display:
                            profile?.status?.lastName === "Hide"
                              ? "none"
                              : "block",
                        }}
                      >
                        <TextField
                          label="نام خانوادگی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("lastName")}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={3}
                        sx={{
                          display:
                            profile?.status?.email === "Hide"
                              ? "none"
                              : "block",
                        }}
                      >
                        <TextField
                          label="ایمیل"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("email")}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={3}
                        sx={{
                          display:
                            profile?.status?.mobile === "Hide"
                              ? "none"
                              : "block",
                        }}
                      >
                        <TextField
                          label="شماره همراه"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("mobile")}
                          disabled={true}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={6}
                        lg={3}
                        sx={{
                          display: "none",
                        }}
                      >
                        <TextField
                          label="نقش"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("roleId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="کد پستی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          inputProps={{ maxLength: 10 }}
                          {...onMakeFormikVariable("postalCode")}
                        />
                      </Grid>

                      {!loadingData && (
                        <>
                          <Grid item xs={12} md={6} lg={3}>
                            <FormControl fullWidth variant="outlined">
                              <InputLabel>استان</InputLabel>
                              <Select
                                label="استان"
                                value={values.provinceId ?? ""}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setFieldValue("provinceId", v || null);
                                  const p = provinces.find((x) => x.id === v);
                                  setFieldValue("province", p?.title || "");
                                  setFieldValue("city", "");
                                  setSelectedProvinceId(v);
                                }}
                              >
                                <MenuItem value="">انتخاب کنید</MenuItem>
                                {provinces.map((p) => (
                                  <MenuItem key={p.id} value={p.id}>
                                    {p.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6} lg={3}>
                            <FormControl
                              fullWidth
                              variant="outlined"
                              disabled={!selectedProvinceId}
                            >
                              <InputLabel>شهرستان</InputLabel>
                              <Select
                                label="شهرستان"
                                value={values.city || ""}
                                onChange={(e) =>
                                  setFieldValue("city", e.target.value)
                                }
                              >
                                <MenuItem value="">انتخاب کنید</MenuItem>
                                {filteredCities.map((c) => (
                                  <MenuItem key={c.id} value={c.cityName}>
                                    {c.cityName}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </>
                      )}

                      <Grid item xs={12} md={6} lg={3}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          error={errors.gender && touched.gender}
                        >
                          <InputLabel>جنسیت</InputLabel>
                          <Select
                            label="جنسیت"
                            onChange={wrapFormikHandleChange(handleChange)}
                            value={values.gender || 0}
                            name="gender"
                          >
                            <MenuItem value={0}>انتخاب کنید</MenuItem>
                            <MenuItem value={1}>مرد</MenuItem>
                            <MenuItem value={2}>زن</MenuItem>
                          </Select>
                          {errors.gender && touched.gender && (
                            <FormHelperText>{errors.gender}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="تلفن ثابت"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("telephon")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <FormControl
                          fullWidth
                          variant="outlined"
                          error={errors.agreementKind && touched.agreementKind}
                        >
                          <InputLabel>نوع شخص</InputLabel>
                          <Select
                            label="نوع شخص"
                            name="agreementKind"
                            value={values.agreementKind || "Real"}
                            onChange={wrapFormikHandleChange(handleChange)}
                          >
                            <MenuItem value="Real">حقیقی</MenuItem>
                            <MenuItem value="Legal">حقوقی</MenuItem>
                          </Select>
                          {errors.agreementKind && touched.agreementKind && (
                            <FormHelperText>{errors.agreementKind}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={12}>
                        <TextField
                          label="آدرس"
                          onChange={wrapFormikHandleChange(handleChange)}
                          multiline
                          rows={3}
                          {...onMakeFormikVariable("address")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FileUploader
                          moduleName={"SLN/CustomerData"}
                          maxFiles={1}
                          label="تصویر کارت ملی (رو)"
                          onChange={(value) => {
                            setFieldValue("nationalCardUrl", value || "");
                          }}
                          baseUrl={getBaseUrl()}
                          placeholder={"تصویر کارت ملی را انتخاب کنید"}
                          accept="image/*"
                          value={values.nationalCardUrl}
                          {...onMakeFormikVariable("nationalCardUrl")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FileUploader
                          moduleName={"SLN/CustomerData"}
                          maxFiles={1}
                          label="تصویر کارت ملی (پشت)"
                          onChange={(value) => {
                            setFieldValue("nationalCardBackUrl", value || "");
                          }}
                          baseUrl={getBaseUrl()}
                          placeholder={"تصویر پشت کارت ملی را انتخاب کنید"}
                          accept="image/*"
                          value={values.nationalCardBackUrl}
                          {...onMakeFormikVariable("nationalCardBackUrl")}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={12}
                        container
                        justifyContent={"flex-end"}
                      >
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToDashboard}
                        >
                          انصراف
                        </Button>

                        <Button
                          disabled={isSubmitting}
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          ذخیره
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ProfilePage;
